"use strict";
/**
 * Adapters that fold a real `WsElementPopupResponse` from the
 * backend into the popup content shapes consumed by the four
 * EntityPopup variants.
 *
 * No mock fallbacks: every field on the rendered popup comes from
 * the backend (canonical element row, current mention, attributes,
 * cross-portfolio rollup, smart summary). Optional fields are
 * simply omitted when the catalogue does not have them yet.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.adaptToTechnology = exports.adaptToMetric = exports.adaptToPersona = exports.adaptToBusinessImpact = exports.mapElementTypeToPopupKind = void 0;
var ai_hub_route_utils_1 = require("../../../utils/ai-hub-route-utils");
var STATUS_COLOR_BY_PRIORITY = {
    Critical: 'red',
    High: 'orange',
    Medium: 'blue',
    Low: 'gray',
};
/**
 * Pick a popup kind from the canonical element type. The backend's
 * granular type union maps onto the four UI popup variants.
 */
exports.mapElementTypeToPopupKind = function (type) {
    if (type === 'persona')
        return 'persona';
    if (type === 'metric')
        return 'metric';
    if (type === 'tech' || type === 'data' || type === 'integration') {
        return 'technology';
    }
    return 'business-impact';
};
var buildUseCases = function (response, currentUseCaseId) {
    return response.usage.items.map(function (item) {
        var _a, _b, _c;
        return ({
            id: item.useCaseId,
            name: item.title && item.title.length > 0 ? item.title : item.useCaseId,
            href: ai_hub_route_utils_1.buildAiHubUseCaseDetailsPath(item.useCaseId),
            subtitle: item.section,
            statusColor: (_c = STATUS_COLOR_BY_PRIORITY[String((_b = (_a = response.element.attributes) === null || _a === void 0 ? void 0 : _a.priority) !== null && _b !== void 0 ? _b : '')]) !== null && _c !== void 0 ? _c : 'blue',
            isCurrent: currentUseCaseId ? item.useCaseId === currentUseCaseId : false,
        });
    });
};
/** Build a 2-letter avatar from a canonical name. */
var buildInitials = function (name) {
    var tokens = name
        .replace(/[^A-Za-z0-9\s-]/g, '')
        .split(/[\s-]+/)
        .filter(function (t) { return t.length > 0; });
    if (tokens.length === 0)
        return '··';
    if (tokens.length === 1)
        return tokens[0].slice(0, 2).toUpperCase();
    return (tokens[0][0] + tokens[1][0]).toUpperCase();
};
/**
 * Choose the most informative description for a popup body. Smart
 * summary (LLM-generated 2-sentence rollup) wins; falls back to the
 * canonical description, then the current mention excerpt, then a
 * neutral placeholder.
 */
var pickDescription = function (response) {
    var _a, _b, _c, _d;
    var summary = (_a = response.element.smartSummary) === null || _a === void 0 ? void 0 : _a.trim();
    if (summary && summary.length > 0)
        return summary;
    var desc = (_b = response.element.description) === null || _b === void 0 ? void 0 : _b.trim();
    if (desc && desc.length > 0)
        return desc;
    var excerpt = (_d = (_c = response.currentMention) === null || _c === void 0 ? void 0 : _c.excerpt) === null || _d === void 0 ? void 0 : _d.trim();
    if (excerpt && excerpt.length > 0)
        return excerpt;
    return 'No description available yet.';
};
// ──────────────────────────────────────────────────────────────────────────
// Typed attribute helpers. Agent attributes are `Record<string, unknown>`
// so we narrow safely without crashing on missing/malformed payloads.
// ──────────────────────────────────────────────────────────────────────────
var asString = function (v) {
    return typeof v === 'string' && v.trim().length > 0 ? v : undefined;
};
var asStringArray = function (v) {
    if (!Array.isArray(v))
        return undefined;
    var out = v.filter(function (x) { return typeof x === 'string' && x.trim().length > 0; });
    return out.length > 0 ? out : undefined;
};
var asNumber = function (v) {
    if (typeof v === 'number' && Number.isFinite(v))
        return v;
    if (typeof v === 'string') {
        var parsed = Number(v);
        if (Number.isFinite(parsed))
            return parsed;
    }
    return undefined;
};
var formatTarget = function (target, unit, delta) {
    var _a;
    var targetStr = (_a = asString(target)) !== null && _a !== void 0 ? _a : (asNumber(target) !== undefined ? String(asNumber(target)) : undefined);
    var unitStr = asString(unit);
    var deltaStr = asString(delta);
    if (targetStr && unitStr)
        return targetStr + " " + unitStr;
    if (targetStr)
        return targetStr;
    if (deltaStr && unitStr)
        return deltaStr + " " + unitStr;
    return deltaStr;
};
var pickBusinessImpactVariant = function (type) {
    if (type === 'pain')
        return 'pain-point';
    if (type === 'risk')
        return 'risk';
    if (type === 'roadmap')
        return 'roadmap';
    if (type === 'budget')
        return 'budget';
    return 'benefit';
};
/** Build the Roadmap-variant payload from canonical attributes. */
var pickRoadmap = function (response) {
    var _a, _b, _c, _d, _e, _f;
    if (response.element.type !== 'roadmap')
        return undefined;
    var attrs = (_a = response.element.attributes) !== null && _a !== void 0 ? _a : {};
    var startDate = (_b = asString(attrs.startDate)) !== null && _b !== void 0 ? _b : asString(attrs.start);
    var endDate = (_c = asString(attrs.endDate)) !== null && _c !== void 0 ? _c : asString(attrs.end);
    var horizon = (_d = asString(attrs.horizon)) !== null && _d !== void 0 ? _d : asString(attrs.timeframe);
    var status = (_e = asString(attrs.status)) !== null && _e !== void 0 ? _e : asString(response.element.status);
    var milestones = (_f = asStringArray(attrs.milestones)) !== null && _f !== void 0 ? _f : asStringArray(attrs.deliverables);
    if (!startDate && !endDate && !horizon && !status && (!milestones || milestones.length === 0)) {
        return undefined;
    }
    return __assign(__assign(__assign(__assign(__assign({}, (startDate ? { startDate: startDate } : {})), (endDate ? { endDate: endDate } : {})), (horizon ? { horizon: horizon } : {})), (status ? { status: status } : {})), (milestones ? { milestones: milestones } : {}));
};
/** Build the Budget-variant payload from canonical attributes. */
var pickBudget = function (response) {
    var _a, _b, _c;
    if (response.element.type !== 'budget')
        return undefined;
    var attrs = (_a = response.element.attributes) !== null && _a !== void 0 ? _a : {};
    var rawAmount = asNumber(attrs.amount);
    var currency = asString(attrs.currency);
    var formattedAmount = rawAmount !== undefined
        ? currency
            ? rawAmount.toLocaleString('en-US') + " " + currency
            : rawAmount.toLocaleString('en-US')
        : asString(attrs.amountDisplay);
    var costType = (_b = asString(attrs.costType)) !== null && _b !== void 0 ? _b : asString(attrs.capexOpex);
    var fundedBy = (_c = asString(attrs.fundedBy)) !== null && _c !== void 0 ? _c : asString(attrs.costCenter);
    var confidence = asString(attrs.confidenceInterval);
    if (!formattedAmount && !costType && !fundedBy && !confidence && !currency)
        return undefined;
    return __assign(__assign(__assign(__assign(__assign({}, (formattedAmount ? { amount: formattedAmount } : {})), (currency ? { currency: currency } : {})), (costType ? { costType: costType } : {})), (fundedBy ? { fundedBy: fundedBy } : {})), (confidence ? { confidence: confidence } : {}));
};
/**
 * Build a {section, excerpt} provenance pair from the current
 * mention so the popup can render the verbatim parsed sentence.
 */
var buildProvenance = function (response) {
    var _a;
    var mention = response.currentMention;
    if (!mention)
        return undefined;
    var excerpt = (_a = mention.excerpt) === null || _a === void 0 ? void 0 : _a.trim();
    if (!excerpt)
        return undefined;
    return __assign(__assign(__assign({ section: mention.section, excerpt: excerpt }, (typeof mention.id === 'number' ? { mentionId: mention.id } : {})), (mention.reviewedBy ? { reviewedBy: mention.reviewedBy } : {})), (mention.role ? { role: mention.role } : {}));
};
/** Pass-through helper so each popup variant gets the same array. */
var pickRelated = function (response) {
    return response.related && response.related.length > 0 ? response.related : undefined;
};
/** Pass owner contact information so popups can render a mailto link. */
var pickOwner = function (response) {
    var owner = response.element.owner;
    if (!owner)
        return undefined;
    var hasName = typeof owner.name === 'string' && owner.name.trim().length > 0;
    var hasEmail = typeof owner.email === 'string' && owner.email.trim().length > 0;
    if (!hasName && !hasEmail)
        return undefined;
    return __assign(__assign({}, (hasName ? { name: owner.name } : {})), (hasEmail ? { email: owner.email } : {}));
};
/** Forward the activity heatmap when the backend supplied non-empty data. */
var pickHeatmap = function (response) {
    var heatmap = response.heatmap;
    if (!heatmap || heatmap.length === 0)
        return undefined;
    var total = heatmap.reduce(function (acc, p) { return acc + p.count; }, 0);
    return total > 0 ? heatmap : undefined;
};
/** Forward coverage gap hints when the backend reported any. */
var pickCoverageGaps = function (response) {
    var gaps = response.coverageGaps;
    return gaps && gaps.length > 0 ? gaps : undefined;
};
exports.adaptToBusinessImpact = function (response, currentUseCaseId) {
    var _a, _b, _c, _d;
    var attrs = (_a = response.element.attributes) !== null && _a !== void 0 ? _a : {};
    var variant = pickBusinessImpactVariant(response.element.type);
    // Category & sub-tags vary per variant
    var category;
    var subTags = [];
    if (variant === 'pain-point') {
        category = asString(attrs.category);
        var severity = asString(attrs.severity);
        if (severity)
            subTags.push({ label: "Severity: " + severity });
    }
    else if (variant === 'risk') {
        category = asString(attrs.riskCategory);
        var impact = asString(attrs.impact);
        var likelihood = asString(attrs.likelihood);
        if (impact)
            subTags.push({ label: "Impact: " + impact });
        if (likelihood)
            subTags.push({ label: "Likelihood: " + likelihood });
    }
    else {
        category = (_c = (_b = asString(attrs.valueType)) !== null && _b !== void 0 ? _b : asString(attrs.categoryName)) !== null && _c !== void 0 ? _c : asString(attrs.phase);
    }
    // Append smart-summary themes as sub-tags so the user sees the
    // auto-derived clusters at a glance.
    for (var _i = 0, _e = (_d = response.element.smartSummaryThemes) !== null && _d !== void 0 ? _d : []; _i < _e.length; _i++) {
        var theme = _e[_i];
        subTags.push({ label: theme });
    }
    // Strategy alignment chips (benefit only)
    var alignedWith = variant === 'benefit' ? asStringArray(attrs.strategyAlignment) : undefined;
    // Quantitative stats — benefit.quantitative or budget direct fields
    var quantitative = [];
    if (variant === 'benefit' && attrs.quantitative && typeof attrs.quantitative === 'object') {
        var q = attrs.quantitative;
        var unit = asString(q.unit);
        var delta = asString(q.delta);
        var min = asNumber(q.min);
        var max = asNumber(q.max);
        if (delta)
            quantitative.push({ label: 'Delta', value: unit ? delta + " " + unit : delta });
        if (min !== undefined)
            quantitative.push({ label: 'Min', value: unit ? min + " " + unit : String(min) });
        if (max !== undefined)
            quantitative.push({ label: 'Max', value: unit ? max + " " + unit : String(max) });
    }
    if (response.element.type === 'budget') {
        var amount = asNumber(attrs.amount);
        var currency = asString(attrs.currency);
        var percentage = asNumber(attrs.percentage);
        if (amount !== undefined) {
            quantitative.push({
                label: 'Amount',
                value: currency ? amount + " " + currency : String(amount),
            });
        }
        if (percentage !== undefined) {
            quantitative.push({ label: 'Share', value: percentage + "%" });
        }
    }
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ variant: variant, title: response.element.canonicalName, description: pickDescription(response) }, (category ? { category: category } : {})), (subTags.length > 0 ? { subTags: subTags } : {})), (alignedWith ? { alignedWith: alignedWith } : {})), (quantitative.length > 0 ? { quantitative: quantitative } : {})), (variant === 'risk' && asStringArray(attrs.mitigations)
        ? { mitigations: asStringArray(attrs.mitigations) }
        : {})), (pickRoadmap(response) ? { roadmap: pickRoadmap(response) } : {})), (pickBudget(response) ? { budget: pickBudget(response) } : {})), { useCases: buildUseCases(response, currentUseCaseId) }), (buildProvenance(response) ? { provenance: buildProvenance(response) } : {})), (pickRelated(response) ? { related: pickRelated(response) } : {})), (pickOwner(response) ? { owner: pickOwner(response) } : {})), (pickHeatmap(response) ? { heatmap: pickHeatmap(response) } : {})), (pickCoverageGaps(response) ? { coverageGaps: pickCoverageGaps(response) } : {}));
};
exports.adaptToPersona = function (response, currentUseCaseId) {
    var _a;
    var attrs = (_a = response.element.attributes) !== null && _a !== void 0 ? _a : {};
    var businessFunction = asString(attrs.businessFunction);
    var seniority = asString(attrs.seniority);
    var specificNeeds = asStringArray(attrs.specificNeeds);
    var category = businessFunction && seniority
        ? businessFunction + " \u00B7 " + seniority
        : (businessFunction !== null && businessFunction !== void 0 ? businessFunction : seniority);
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ initials: buildInitials(response.element.canonicalName), name: response.element.canonicalName, description: pickDescription(response) }, (category ? { category: category } : {})), (specificNeeds ? { specificNeeds: specificNeeds } : {})), (response.element.smartSummaryThemes && response.element.smartSummaryThemes.length > 0
        ? { painPointsAddressed: response.element.smartSummaryThemes }
        : {})), { useCases: buildUseCases(response, currentUseCaseId), totalUseCases: response.usage.count }), (buildProvenance(response) ? { provenance: buildProvenance(response) } : {})), (pickRelated(response) ? { related: pickRelated(response) } : {})), (pickOwner(response) ? { owner: pickOwner(response) } : {})), (pickHeatmap(response) ? { heatmap: pickHeatmap(response) } : {})), (pickCoverageGaps(response) ? { coverageGaps: pickCoverageGaps(response) } : {}));
};
exports.adaptToMetric = function (response, currentUseCaseId) {
    var _a;
    var attrs = (_a = response.element.attributes) !== null && _a !== void 0 ? _a : {};
    var category = asString(attrs.category);
    var target = formatTarget(attrs.targetValue, attrs.unit, undefined);
    var measurement = asString(attrs.measurementApproach);
    var visualization = asString(attrs.visualization);
    var dataSource = asString(attrs.dataSource);
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ name: response.element.canonicalName, definition: pickDescription(response) }, (category ? { category: category } : {})), (target ? { target: target } : {})), (measurement ? { measurementMethod: measurement } : {})), (visualization ? { visualizationType: visualization } : {})), (dataSource ? { poweredBy: [dataSource] } : {})), (response.element.smartSummaryThemes && response.element.smartSummaryThemes.length > 0
        ? { strategy: response.element.smartSummaryThemes }
        : {})), { useCases: buildUseCases(response, currentUseCaseId).map(function (u) { return ({
            id: u.id,
            name: u.name,
            href: u.href,
            subtitle: u.subtitle,
            isCurrent: u.isCurrent,
        }); }) }), (buildProvenance(response) ? { provenance: buildProvenance(response) } : {})), (pickRelated(response) ? { related: pickRelated(response) } : {})), (pickOwner(response) ? { owner: pickOwner(response) } : {})), (pickHeatmap(response) ? { heatmap: pickHeatmap(response) } : {})), (pickCoverageGaps(response) ? { coverageGaps: pickCoverageGaps(response) } : {}));
};
exports.adaptToTechnology = function (response, currentUseCaseId) {
    var _a, _b, _c;
    var attrs = (_a = response.element.attributes) !== null && _a !== void 0 ? _a : {};
    var vendor = asString(attrs.vendor);
    var techCategory = asString(attrs.techCategory);
    var readiness = (_b = asString(attrs.readiness)) !== null && _b !== void 0 ? _b : asString(response.element.status);
    var costModel = asString(attrs.costModel);
    var region;
    var sso;
    var dataClassification;
    if (attrs.securityPosture && typeof attrs.securityPosture === 'object') {
        var sec = attrs.securityPosture;
        region = asString(sec.region);
        if (typeof sec.sso === 'boolean')
            sso = sec.sso ? 'Yes' : 'No';
        dataClassification = asString(sec.dataClassification);
    }
    var integration = [];
    if (vendor)
        integration.push({ key: 'Vendor', value: vendor });
    if (techCategory)
        integration.push({ key: 'Category', value: techCategory });
    if (costModel)
        integration.push({ key: 'Cost model', value: costModel });
    if (region)
        integration.push({ key: 'Region', value: region });
    if (sso)
        integration.push({ key: 'SSO', value: sso });
    if (dataClassification)
        integration.push({ key: 'Data classification', value: dataClassification });
    var badges = [];
    if (readiness)
        badges.push(readiness);
    for (var _i = 0, _d = (_c = response.element.smartSummaryThemes) !== null && _c !== void 0 ? _c : []; _i < _d.length; _i++) {
        var theme = _d[_i];
        badges.push(theme);
    }
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ name: response.element.canonicalName, initials: buildInitials(response.element.canonicalName), roleInSolution: pickDescription(response) }, (badges.length > 0 ? { badges: badges } : {})), (integration.length > 0 ? { integration: integration } : {})), (techCategory ? { partOfStack: [techCategory] } : {})), { useCases: buildUseCases(response, currentUseCaseId) }), (buildProvenance(response) ? { provenance: buildProvenance(response) } : {})), (pickRelated(response) ? { related: pickRelated(response) } : {})), (pickOwner(response) ? { owner: pickOwner(response) } : {})), (pickHeatmap(response) ? { heatmap: pickHeatmap(response) } : {})), (pickCoverageGaps(response) ? { coverageGaps: pickCoverageGaps(response) } : {}));
};
